import React, { useContext, useRef, useState } from 'react'
import { Formik } from 'formik'
import { Button, Form, Grid, GridCell, NotificationInline, NotificationManagerContext,
   Spacer, Text, View } from 'oio-react'
import { VisibilityIcon } from 'assets/icons'
import { adminUrl } from 'config/constants/urls'
import { Textarea } from 'src/components/ConnectedForm'
import HtmlEditor from 'src/components/utils/HtmlEditor'
import ImageUpload from 'src/components/utils/ImageUpload'
import ProtoHeadingBlock from 'src/components/Settings/ProtoHeadingBlock'
import ProtoReturnBar from 'src/components/Settings/ProtoReturnBar'
import { useFileUploads } from 'src/components/utils'
import { useOrganization, useUpdateOrganization } from 'src/graphql/hooks'
import ThemeContext from 'src/libraries/contexts/Theme'

const HomeSettings = () => {
   const descriptionInputRef = useRef(null)
   const [error, setError] = useState(null)
   const { showNotification } = useContext(NotificationManagerContext)
   const theme = useContext(ThemeContext)

   const { organization } = useOrganization()
   const { updateOrganization } = useUpdateOrganization()
   const { uploadFile, uploadedFiles, uploadsInProgress, resetFileUploadState } =
      useFileUploads({ targetType: 'organization' })

   const submitButtonMode = uploadsInProgress.length > 0 ? 'disabled' : 'normal'

   const handleUpdateOrganization = async (values, actions) => {
      try {
         await updateOrganization({
            tagline: values.tagline,
            description: descriptionInputRef.current.editor.html.get(true),
            ...uploadedFiles
         })

         showNotification({
            message: 'Home App saved successfully',
            title: 'Success!',
            type: 'success'
         })

         resetFileUploadState()
      } catch (err) {
         setError(err?.graphQLErrors?.map(e => e.message).join(', ') || err.message)
      } finally {
         actions.setSubmitting(false)
      }
   }

   return (
      <Formik
         initialValues={{ tagline: organization.tagline }}
         onSubmit={handleUpdateOrganization}
         render={({ handleSubmit, isSubmitting }) => (
            <Form
               elementAppearance="plain"
               elementBackgroundColor="#eee"
               elementFocusBackgroundColor="#f3f3f3"
               onSubmit={handleSubmit}>
               <ProtoReturnBar
                  display="block[a-c] none[d-f]"
                  returnLinkName="Admin Workspace"
                  returnLinkTo={adminUrl}
               />
               <View display="flex" justifyContent="center">
                  <View
                     width="100%"
                     maxWidth="100%[a-c] 600px[d-f]"
                     padding="40px 20px">
                     <View
                        width="100%"
                        display="flex"
                        flexFlow="column[a-c] row[d-f]">
                        <View flex="1 1 auto" paddingRight="30px[b-f]">
                           <ProtoHeadingBlock
                              title="Home"
                              description="Edit the contents of the Home Page"
                           />
                        </View>
                        <View display="flex" flex="0 0 auto" paddingTop="10px">
                           <Button
                              linkTo="/"
                              name="View"
                              color="#ddd"
                              textColor="#333"
                              size="md"
                              textSize="0.9"
                              padding="15px">
                              <View display="flex" alignItems="center" marginRight="5px">
                                 <VisibilityIcon
                                    width="16px"
                                    height="16px"
                                    color="#333"
                                    strokeWidth="2px"
                                 />
                              </View>
                           </Button>
                           <Spacer orientation="vertical" size="1" />
                           <Button
                              type="submit"
                              name="Save Changes"
                              mode={isSubmitting ? 'loading' : submitButtonMode}
                              color={theme.protoSettingsHighlightBackgroundColor}
                              textColor={theme.protoSettingsHighlightForegroundColor}
                              textWeight="medium"
                              size="md"
                              textSize="0.9"
                              padding="25px"
                           />
                        </View>
                     </View>
                     <Spacer size="4" />
                     {error && (
                        <NotificationInline
                           borderRadius="3px"
                           textSize="2"
                           type="error"
                           title="Error"
                           message={error.graphQLErrors.map(e => e.message).join(', ') || error.message}
                        />
                     )}
                     <View
                        width="100%"
                        padding="20px 20px 15px 20px"
                        borderRadius="3px"
                        backgroundColor="#fff">
                        <Grid spacing="10px">
                           <GridCell colspan="4">
                              <Text size="2" weight="medium">
                                 Title
                              </Text>
                              <Text size="1.5" color="#999">
                                 (max 140 characters)
                              </Text>
                           </GridCell>
                           <GridCell colspan="8">
                              <Textarea
                                 rows="3"
                                 maxLength="140"
                                 size="sm"
                                 name="tagline"
                                 placeholder="Title"
                              />
                           </GridCell>
                           <GridCell colspan="4">
                              <Text size="2" weight="medium">
                                 Description
                              </Text>
                           </GridCell>
                           <GridCell colspan="8">
                              <HtmlEditor
                                 ref={descriptionInputRef}
                                 config={{ height: 150 }}
                                 initialValue={organization.description}
                              />
                           </GridCell>
                           <GridCell colspan="4">
                              <Text size="2" weight="medium">
                                 Cover Image
                              </Text>
                           </GridCell>
                           <GridCell colspan="8">
                              <View
                                 float="left"
                                 width="150px"
                                 padding="5px"
                                 border="1px solid #e5e5e5"
                                 borderRadius="2px">
                                 <ImageUpload
                                    onFileChange={uploadFile}
                                    name="coverMedia"
                                    purpose="coverPhoto"
                                    src={organization.coverMedia?.file.thumbnailUrlW480}
                                 />
                              </View>
                           </GridCell>
                        </Grid>
                     </View>
                     <Spacer size="6" />
                  </View>
               </View>
            </Form>
         )}
      />
   )
}

export default HomeSettings
